import { FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import { Component, computed, inject, input, output } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { MedicalComponentSearchPopoverComponent } from 'guideline/feature-component-search';
import { produce } from 'immer';
import { map, switchMap } from 'rxjs';
import { ComponentConverter, GuidelineService } from 'shared/domain';
import { ButtonDirective, IconDirective, PopoverService } from 'shared/ui-common';
import { isTruthy, OrganisationSettingsService } from 'shared/util-common';

import { FlowchartStartNodeDto } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { CustomisableFlowchartElementComponent } from '../../../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/customisable-flowchart-element/customisable-flowchart-element.component';
import { FlowchartNodeConfig } from '../../../../../../../../../settings-page/src/lib/organisation-settings-page/flowchart-customisations-tab/flowchart-customisations-settings-tab.component';
import { DividerComponent } from '../../../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { TitleDescriptionComponent } from '../../../../chapter-sidebar-wrapper/components/title-description/title-description.component';
import {
  GuidelineSidebarBasics,
  GuidelineSidebarBasicsComponent,
} from '../../../../guideline-sidebar-basics/guideline-sidebar-basics.component';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    TitleDescriptionComponent,
    MatButtonModule,

    MatIconModule,
    IconDirective,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
    ButtonDirective,
    CustomisableFlowchartElementComponent,
  ],
  selector: 'sidebar-finding-tab',
  templateUrl: './finding-tab.component.html',
  styleUrls: ['./finding-tab.component.scss'],
})
export class FindingTabComponent {
  private popoverService = inject(PopoverService);
  private componentConverter = inject(ComponentConverter);
  protected organisationSettingsService = inject(OrganisationSettingsService);
  private guidelineService = inject(GuidelineService);

  public sidebarElement = input.required<FlowchartStartNodeDto>();

  public flowchartElementDataChanged = output<Partial<FlowchartStartNodeDto>>();

  protected renderDescription = true;
  protected component = new FormControl();

  protected organisationSettings = computed(
    () => this.guidelineService.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  protected colorPresets = computed(() => {
    return this.organisationSettings().customisations?.colorPresets ?? [];
  });

  protected hasColorPresets = computed(() => {
    return this.colorPresets().length > 0;
  });

  protected flowchartNodeConfig = computed<FlowchartNodeConfig>(() => {
    const colorPresets = this.colorPresets();
    const organisationCustomisations = this.organisationSettings().customisations?.flowchart;
    const elementColorPreset = this.sidebarElement().colorPresetId;

    const colorPreset = colorPresets.find((preset) => preset.id === (elementColorPreset ?? organisationCustomisations?.start));

    return {
      type: 'start',
      content: 'Start',
      colorPreset,
    };
  });

  protected enableSearch($event: MouseEvent) {
    if (!this.sidebarElement().guidelineAction) {
      this.openSearchDialog({
        x: $event.clientX,
        y: $event.clientY,
      });
    }
  }

  protected openSearchDialog(point: FlexibleConnectedPositionStrategyOrigin) {
    this.popoverService
      .open(MedicalComponentSearchPopoverComponent, point, {
        width: 400,
        hasBackdrop: false,
        data: {
          definition: this.component,
          findingFilterActive: false,
          activityFilterActive: true,
          observationFilterActive: false,
          enableMultiSearch: true,
          sidebarElement: this.sidebarElement,
          isFilterSearch: false,
          allowProposedComponents: false,
        },
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        map((result) => result.value),
        isTruthy(),
        switchMap((component) => this.componentConverter.convertActivityToGuidelineAction(component)),
      )
      .subscribe(({ component }) => {
        this.flowchartElementDataChanged.emit({
          guidelineAction: component,
          guidelineActionId: component.id,
        });
      });
  }

  protected onColorPresetChanged(colorPresetId: string): void {
    this.flowchartElementDataChanged.emit({ colorPresetId });
  }

  protected onShowDescriptionChange(showDescription: boolean) {
    this.flowchartElementDataChanged.emit({ showDescription });
  }

  protected onDescriptionChanged(description: string) {
    this.flowchartElementDataChanged.emit({ guidelineCriteria: { description } });
  }

  protected onTitleChanged(title: string) {
    this.flowchartElementDataChanged.emit({ guidelineCriteria: { title } });
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newComponent = produce({ ...this.sidebarElement() }, (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
      draft.resourceTags = basics.resourceTags;
    });

    this.flowchartElementDataChanged.emit(newComponent);
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
}
